import React, { PropsWithChildren, ReactElement } from 'react';

import { Dialog, Transition } from '@headlessui/react';
import cn from 'classnames';

import styles from './Drawer.module.css';
import { Button } from '../button/Button';
import { Icon } from '../icon/Icon';

const overlayTransitionProps = {
  enter: styles.overlayTransition,
  enterFrom: styles.overlayTransitionClosed,
  leave: styles.overlayTransition,
  leaveTo: styles.overlayTransitionClosed,
  className: styles.overlay,
  'aria-hidden': true,
};

const drawerTransitionProps = (position: 'left' | 'right') => {
  const side = position === 'left' ? '' : 'Right';
  return {
    enter: styles.drawerTransition,
    leave: styles.drawerTransition,
    enterFrom: styles[`drawerTransitionClosed${side}`],
    leaveTo: styles[`drawerTransitionClosed${side}`],
    className: cn(styles.drawerWrapper, {
      [styles.drawerWrapperRight]: position === 'right',
    }),
  };
};

type DrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  position?: 'left' | 'right';
  title?: string | ReactElement | ReactElement[];
  subTitle?: string | ReactElement | ReactElement[];
} & React.HTMLAttributes<HTMLDivElement>;

export const Drawer = ({
  position = 'right',
  isOpen,
  onClose,
  title,
  subTitle,
  children,
  ...props
}: PropsWithChildren<DrawerProps>) => {
  return (
    <Transition show={isOpen}>
      <Dialog onClose={onClose} className={styles.container}>
        <Transition.Child
          data-testid="drawerOverlay"
          {...overlayTransitionProps}
        ></Transition.Child>
        <Transition.Child {...drawerTransitionProps(position)}>
          <Dialog.Panel className={styles.panel} {...props}>
            <div className={styles.closeIcon}>
              <Button
                data-testid="closeIcon"
                iconOnly
                variant="tertiary"
                size="small"
                onClick={onClose}
                type="button"
                aria-label="close drawer"
              >
                <Icon icon="timesDefault" />
              </Button>
            </div>
            {(title || subTitle) && (
              <div className={styles.header}>
                {title && (
                  <Dialog.Title
                    data-testid="modal-title"
                    className={styles.title}
                  >
                    {title}
                  </Dialog.Title>
                )}
                {subTitle && (
                  <Dialog.Description
                    data-testid="modal-subtitle"
                    className={styles.subTitle}
                  >
                    {subTitle}
                  </Dialog.Description>
                )}
              </div>
            )}
            <div className={styles.childWrapper}>{children}</div>
          </Dialog.Panel>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
};
